import {
    Duration,
    format,
    formatDistance,
    formatDuration,
    FormatDurationOptions,
    intervalToDuration,
    isSameDay,
    isSameMonth,
    isSameYear,
    isToday,
    isTomorrow,
    isValid,
    Locale,
    parseISO,
} from 'date-fns'
import { enUS, hu } from 'date-fns/locale'
import i18n from 'i18next'

export const dateLocales: Map<string, Locale> = new Map([
    ['hu', hu],
    ['en', enUS],
])

export const getCurrentDateLocale = (): Locale => dateLocales.get(i18n.language) || hu
export const getCurrentDateLocaleCode = (): string => i18n.language || 'hu'

type DateInput = Date | string | number | null | undefined

export const formatDate = (date: DateInput, formatStr = 'yyyy.MM.dd'): string => {
    if (date == null) return ''
    const d = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(d.getTime())) return ''
    return format(d, formatStr, { locale: getCurrentDateLocale() })
}

export const formatDateToYMD = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export const formatDateWithMonth = (date: DateInput): string => formatDate(date, 'yyyy. MMMM dd.')

export const formatDateOnly = (dateOnly: string, formatStr = 'yyyy. MMMM d'): string => {
    if (!dateOnly) return ''
    const parsed = parseISO(dateOnly)
    if (!isValid(parsed)) return ''
    return format(parsed, formatStr, { locale: getCurrentDateLocale() })
}

export const formatDateWithTime = (date: DateInput): string => formatDate(date, 'yyyy.MM.dd HH:mm')

export const formatDatePickerLabel = (date: DateInput, withTimePicker?: boolean): string =>
    withTimePicker ? formatDateWithTime(date) : formatDate(date)

export const formatDateMonthName = (date: DateInput): string => formatDate(date, 'yyyy. MMMM dd.')

export const formatTime = (date: DateInput): string => formatDate(date, 'HH:mm')

export const formatTimeDuration = (duration: Duration, options?: FormatDurationOptions): string =>
    formatDuration(duration, { ...options, locale: getCurrentDateLocale() })

export const formatIntervalTimeDuration = ({ start, end }: { start: Date; end: Date }) => {
    const duration = intervalToDuration({ start, end })

    const hours =
        (duration.hours ?? 0) +
        (duration.days ?? 0) * 24 +
        (duration.weeks ?? 0) * 7 * 24 +
        (duration.months ?? 0) * 30 * 24 +
        (duration.years ?? 0) * 365 * 24

    return [hours, duration.minutes, duration.seconds]
        .map((value) => String(value ?? 0).padStart(2, '0'))
        .join(':')
}

export const formatInterval = ({ start, end }: { start: Date; end: Date }): string => {
    const date = format(start, 'yyyy.MM.dd', { locale: getCurrentDateLocale() })
    const startTime = format(start, 'HH:mm', { locale: getCurrentDateLocale() })
    const endTime = format(end, 'HH:mm', { locale: getCurrentDateLocale() })

    return `${date} ${startTime} - ${endTime}`
}

export const formatDistanceToNow = (date: Date): string => {
    return formatDistance(date, new Date(), { locale: hu, addSuffix: true })
}

export const secondsToHHMM = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export const convertSecondsToDecimalHours = (seconds: number) => {
    return Math.round((seconds / 3600) * 10) / 10
}

export const formatDateRange = (
    startDate: string | Date,
    endDate?: string | Date | null,
): string => {
    const start = new Date(startDate)
    const end = endDate ? new Date(endDate) : null
    const locale = getCurrentDateLocale()
    const fmt = 'yyyy. MMMM dd.'

    if (!end || isSameDay(start, end)) {
        return format(start, fmt, { locale })
    }

    return `${format(start, fmt, { locale })} – ${format(end, fmt, { locale })}`
}

export const formatDateRangeShort = (
    startDate: string | Date | null | undefined,
    endDate?: string | Date | null,
): string => {
    if (startDate == null) return ''
    const start = startDate instanceof Date ? startDate : new Date(startDate)
    if (Number.isNaN(start.getTime())) return ''
    const endCandidate = endDate ? (endDate instanceof Date ? endDate : new Date(endDate)) : null
    const end = endCandidate && !Number.isNaN(endCandidate.getTime()) ? endCandidate : null
    const locale = getCurrentDateLocale()

    const startStr = format(start, 'yyyy.MM.dd', { locale })

    if (!end || isSameDay(start, end)) {
        return startStr
    }

    if (!isSameYear(start, end)) {
        return `${startStr} - ${format(end, 'yyyy.MM.dd', { locale })}`
    }

    if (!isSameMonth(start, end)) {
        return `${startStr} - ${format(end, 'MM.dd', { locale })}`
    }

    return `${startStr}-${format(end, 'dd', { locale })}`
}

export const formatUpcomingAppointmentDate = (date: Date): string => {
    if (isToday(date)) {
        return i18n.t('common.ui.today')
    }
    if (isTomorrow(date)) {
        return i18n.t('common.ui.tomorrow')
    }
    return format(date, 'EEE', { locale: getCurrentDateLocale() })
}
